import { Injectable } from '@angular/core';
import { defaultFollowUpOptions, FOLLOW_UP_WHEN_OPTIONS, FollowUpMenuItem, FollowUpOption, FollowUpSelection } from '@models/follow-up-options.model';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

/**
 * The signed-in user's Follow Up menu presets for the current office, and the date/time maths
 * behind them.
 *
 * Backed by its own endpoints (`GET|PUT /follow-up-options`), spec in
 * `docs/backend/leads/follow-up-options.md`. The settings page and the lead Follow Up menu both read
 * `options$`, so a save on one shows up in the other without a reload.
 *
 * Whenever the GET fails (e.g. a 403 for an office the user isn't in), the menu keeps the Remmi defaults.
 *
 * Dates follow the lead page's long-standing rule: the day is worked out in Brisbane time
 * (UTC+10), whatever the browser's zone.
 */
@Injectable({ providedIn: 'root' })
export class FollowUpOptionsService {
  private readonly endpoint = '/follow-up-options';
  private readonly _options$ = new BehaviorSubject<FollowUpOption[]>(defaultFollowUpOptions());
  private loadedForKey: string = null;

  constructor(private api: ApiService) {}

  get options$(): Observable<FollowUpOption[]> {
    return this._options$.asObservable();
  }

  get options(): FollowUpOption[] {
    return this._options$.value;
  }

  /**
   * Fetches the saved list once per user + office. Keyed on both rather than a plain flag because
   * this service outlives a logout and an office switch — the next agent, or the same agent in
   * another office, must not inherit the previous menu.
   */
  ensureLoaded(): void {
    const key = `${this.api.getUserId()}|${this.api.getOfficeId()}`;
    if (!this.api.getUserId() || this.loadedForKey === key) {
      return;
    }
    this.loadedForKey = key;
    this.load().subscribe({
      error: () => (this.loadedForKey = null),
    });
  }

  /** GET /follow-up-options — this user's list in this office; `data.options: null` means "Remmi defaults". */
  load(): Observable<FollowUpOption[]> {
    return this.api.get(this.endpoint, this.scope()).pipe(
      map((res: any) => this.resolve(res?.data?.options)),
      tap(options => this._options$.next(options))
    );
  }

  /** PUT /follow-up-options — replaces the whole list for this user + office; the array order is the menu order. */
  save(options: FollowUpOption[]): Observable<FollowUpOption[]> {
    return this.api.put(this.endpoint, { ...this.scope(), options }).pipe(
      // Prefer the stored copy the API echoes back; fall back to what was sent.
      map((res: any) => this.resolve(res?.data?.options ?? options)),
      tap(saved => this._options$.next(saved))
    );
  }

  /** Enabled options, in the user's order, with the date/time each one would set right now. */
  menuItems(options: FollowUpOption[] = this.options): FollowUpMenuItem[] {
    const today = this.brisbaneToday();
    return options
      .filter(option => option.isEnabled)
      .map(option => ({ id: option.id, label: option.label, display: this.displayFor(option, today) }));
  }

  selectionFor(optionId: string): FollowUpSelection | null {
    const option = this.options.find(itm => itm.id === optionId);
    if (!option) {
      return null;
    }
    const target = this.targetDay(option, this.brisbaneToday());
    return {
      optionId: option.id,
      // 08:00Z on the target day is exactly what the task API has always been sent for a quick follow
      // up; the real time of day travels separately in `task_time`.
      dueDate: target.clone().hour(8).minute(0).second(0).millisecond(0).utc().toISOString(),
      taskTime: this.taskTimeFor(option).format('h:mma'),
    };
  }

  /** Which enabled option would have produced a follow up on this date — drives the menu highlight. */
  matchOptionId(dueDate: string | Date): string | null {
    if (!dueDate) {
      return null;
    }
    const today = this.brisbaneToday();
    const due = moment(dueDate).format('DD-MM-YYYY');
    const match = this.options.find(option => option.isEnabled && this.targetDay(option, today).format('DD-MM-YYYY') === due);
    return match?.id ?? null;
  }

  dayOffset(when: string, today: moment.Moment = this.brisbaneToday()): number {
    const rule = FOLLOW_UP_WHEN_OPTIONS.find(itm => itm.value === when);
    if (!rule) {
      return 0;
    }
    if (rule.weekday !== undefined) {
      // "Next Tuesday" asked on a Tuesday means a week today, never today itself.
      const diff = (rule.weekday - today.day() + 7) % 7;
      return diff === 0 ? 7 : diff;
    }
    return rule.days ?? 0;
  }

  /** Options are stored per user per office. */
  private scope(): { user_id: string; office_id: string } {
    return { user_id: this.api.getUserId(), office_id: this.api.getOfficeId() };
  }

  /** No saved list yet (or a malformed one) — fall back to the Remmi defaults. */
  private resolve(saved: FollowUpOption[] | null | undefined): FollowUpOption[] {
    if (!Array.isArray(saved)) {
      return defaultFollowUpOptions();
    }
    return saved
      .filter(option => option?.id && option?.label)
      .map(option => ({
        ...option,
        isEnabled: option.isEnabled !== false,
        when: option.when || 'next_day',
        time: option.time || '08:00',
      }));
  }

  /** Brisbane wall-clock "now", held as a UTC moment so its fields read as Brisbane time. */
  private brisbaneToday(): moment.Moment {
    return moment.utc().add(10, 'hours');
  }

  private targetDay(option: FollowUpOption, today: moment.Moment): moment.Moment {
    return today.clone().add(this.dayOffset(option.when, today), 'days');
  }

  /**
   * The option's time as a local moment. A same-day option whose time has already gone by falls
   * back to the old "Later Today" rule — two hours from now, rounded to the half hour — so it
   * never books a follow up in the past.
   */
  private taskTimeFor(option: FollowUpOption): moment.Moment {
    const [hours, minutes] = (option.time || '08:00').split(':').map(Number);
    const time = moment().hour(hours).minute(minutes).second(0).millisecond(0);
    if (this.dayOffset(option.when) === 0 && !time.isAfter(moment())) {
      const fallback = moment().add(2, 'hours').second(0).millisecond(0);
      const rounded = Math.round(fallback.minute() / 30) * 30;
      return fallback.minute(0).add(rounded, 'minutes');
    }
    return time;
  }

  private displayFor(option: FollowUpOption, today: moment.Moment): string {
    const offset = this.dayOffset(option.when, today);
    const time = this.taskTimeFor(option);
    if (offset === 0) {
      return time.format('h:mma');
    }
    const target = this.targetDay(option, today);
    const isWeekday = FOLLOW_UP_WHEN_OPTIONS.find(itm => itm.value === option.when)?.weekday !== undefined;
    // Far-off and named-weekday options carry the date too, so "Mon" can't be misread as this Monday.
    const dayFormat = isWeekday || offset >= 14 ? 'ddd, D MMM' : 'ddd';
    return `${target.format(dayFormat)}, ${time.format('hh:mma')}`;
  }
}
